import React, { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  selectedProvincesState,
  selectedPemdaState,
  sidebarisexpandAtom,
  selectedJenisTkdState,
  months,
  startMonthAtom,
  endMonthAtom,
} from "states/recoil";
import {
  Center,
  Spinner,
  Text,
  CardBody,
  CardHeader,
  Heading,
  IconButton,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from "@chakra-ui/react";
import { HiOutlineDownload, HiArrowsExpand } from "react-icons/hi";
import { FaChartLine } from "react-icons/fa";
import Chart from "react-apexcharts";
import { DownloadImage } from "components/functions/DownloadImage";
import { ExportToExcel } from "components/functions/ExportToExcel";
import { ToggleFullScreen } from "components/functions/ToggleFullscreen";
import { UseTogglePopUp } from "components/functions/UseTogglePopUp";
import { tahunAnggaranAtom } from "states/recoil";

function LineChartMonthly({ data, titleChart }) {
  //ref to downloadImage
  const chartContainerRef = useRef(null);

  //recoil-state
  const [selectedProvinces, setSelectedProvinces] = useRecoilState(
    selectedProvincesState
  );
  const [selectedPemda, setSelectedPemda] = useRecoilState(selectedPemdaState);
  const [selectedJenisTKD, setSelectedJenisTKD] = useRecoilState(
    selectedJenisTkdState
  );
  const monthArray = useRecoilValue(months);
  const [selectedStartMonth] = useRecoilState(startMonthAtom);
  const [selectedEndMonth] = useRecoilState(endMonthAtom);

  //local state
  const [isLoading, setIsLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const { isOpen, openPopup, closePopup } = UseTogglePopUp();

  //chart state
  const [lineChartData, setLineChartData] = useState({
    categories: [],
    series: [],
  });

  //get the data
  useEffect(() => {
    setRawData(data);
  }, [data]);

  //Set Chart Data
  useEffect(() => {
    setIsLoading(true);

    if (
      !rawData.length ||
      !selectedProvinces.length ||
      !selectedPemda.length ||
      !selectedJenisTKD.length
    ) {
      setLineChartData({
        categories: [],
        series: [],
      });
      return;
    }

    const filteredData = rawData.filter(
      (row) =>
        selectedProvinces.includes(row["NM PROV"]) &&
        selectedPemda.includes(row["NM PEMDA"]) &&
        selectedJenisTKD.includes(row["JENIS TKD"])
    );

    const monthlySums = Array(12).fill(0); // Index 0 = Jan, 11 = Dec

    filteredData.forEach((row) => {
      for (let i = 1; i <= 12; i++) {
        const monthKey = `REALISASI ${String(i).padStart(2, "0")}`;
        if (row[monthKey]) {
          monthlySums[i - 1] += parseFloat(row[monthKey]) || 0;
        }
      }
    });

    const startIndex = monthArray.indexOf(selectedStartMonth);
    const endIndex = monthArray.indexOf(selectedEndMonth);

    if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) {
      setLineChartData({ categories: [], series: [] });
      setIsLoading(false);
      return;
    }

    // Ambil label bulan langsung dari array string monthArray tanpa map .label
    const monthLabels = monthArray.slice(startIndex, endIndex + 1);
    const filteredMonthlySums = monthlySums.slice(startIndex, endIndex + 1);

    setLineChartData({
      categories: monthLabels,
      series: [
        {
          name: "Realisasi Bulanan",
          data: filteredMonthlySums.map((val) => val / 1_000_000_000),
        },
      ],
    });
    setIsLoading(false);
  }, [
    rawData,
    selectedProvinces,
    selectedPemda,
    selectedJenisTKD,
    selectedEndMonth,
    selectedStartMonth,
    tahunAnggaranAtom,
  ]);

  //set chart options
  const lineChartOptions = {
    chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
    xaxis: { categories: lineChartData.categories },
    yaxis: {
      labels: {
        formatter: (val) => `${val.toFixed(2)} M`,
      },
    },
    tooltip: {
      y: {
        formatter: (val) => {
          return (
            val.toLocaleString("id-ID", {
              style: "currency",
              currency: "IDR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + " M"
          );
        },
      },
    },
    colors: ["#00E396"],
    stroke: { curve: "smooth" },
    markers: {
      size: 6,
      strokeWidth: 2,
      strokeColors: "#fff",
      fillOpacity: 1,
      shape: "circle",
      hover: {
        size: 8,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        const milyar = val;
        return (
          milyar.toLocaleString("id-ID", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + " M"
        );
      },
      style: {
        fontSize: "12px",
        colors: ["#858585"],
      },
      background: {
        enabled: true,
        color: "#ffffff",
        padding: 4,
        borderRadius: 3,
        opacity: 1,
      },
      offsetY: -15,
    },
  };

  return (
    <>
      <CardHeader
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex align="center" gap={4}>
          <IconButton icon={<FaChartLine />} aria-label="chart" size="sm" />
          <Heading size="sm">{titleChart}</Heading>
        </Flex>
        <Flex gap={4}>
          <IconButton
            icon={<HiArrowsExpand />}
            aria-label="export"
            size="sm"
            onClick={openPopup}
          />
          <Menu>
            <MenuButton as={Button} size="sm">
              <HiOutlineDownload />
            </MenuButton>
            <MenuList
              maxHeight="300px"
              overflowY="auto"
              minWidth="250px"
              zIndex={3}
            >
              <MenuItem
                onClick={() =>
                  DownloadImage(chartContainerRef.current, titleChart)
                }
                title={titleChart}
              >
                Unduh sebagai gambar (.PNG)
              </MenuItem>
              <MenuItem
                onClick={() =>
                  ExportToExcel(lineChartData, `${titleChart}.xlsx`)
                }
              >
                Unduh sebagai spreadsheet (.xlsx)
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </CardHeader>
      <CardBody ref={chartContainerRef}>
        {isLoading ? (
          <Center w="100%" h="500px">
            <Spinner size="xl" color="blue.500" />
          </Center>
        ) : rawData.length === 0 ? (
          <Center w="100%" h="500px">
            <Text>Tidak ada data yang ditampilkan.</Text>
          </Center>
        ) : (
          <Chart
            options={lineChartOptions}
            series={lineChartData.series}
            type="line"
            height={400}
          />
        )}
      </CardBody>

      {isOpen && (
        <ToggleFullScreen onClose={closePopup} rawData={rawData}>
          {/* Render the expanded chart here */}
          <div
            style={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            {isLoading ? (
              <Center w="100%" h="500px">
                <Spinner size="xl" color="blue.500" />
              </Center>
            ) : rawData.length === 0 ? (
              <Center w="100%" h="500px">
                <Text>Tidak ada data yang ditampilkan.</Text>
              </Center>
            ) : (
              <Center w="100%" h="100%">
                <Chart
                  options={lineChartOptions}
                  series={lineChartData.series}
                  type="line"
                  height="100%"
                  width="400%"
                />
              </Center>
            )}
          </div>
        </ToggleFullScreen>
      )}
    </>
  );
}

export default LineChartMonthly;
